import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getStaffThread,
  getStaffMessages,
  postStaffMessage,
  markStaffRead,
  createChatInvite,
  getStaffAttachmentBlob,
} from "@/lib/chat";
import Button from "@/components/ui/Button";
import { connectStaffChatSocket } from "@/lib/chat_ws";

const unwrap = (res) =>
  res && typeof res === "object" && "data" in res ? res.data : res;

function fmtTime(val) {
  const d = val ? new Date(val) : null;
  if (!d || isNaN(d)) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(
    2,
    "0"
  )}`;
}

function normalizeError(e, fallback) {
  const data = e?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (typeof data?.detail === "string") return data.detail;
  try {
    return JSON.stringify(data);
  } catch {
    return fallback;
  }
}

function isApptNotFoundForBusinessError(e) {
  const data = e?.response?.data;
  const msg = data?.appointment_id;
  return (
    e?.response?.status === 400 &&
    Array.isArray(msg) &&
    msg.some((x) =>
      String(x).toLowerCase().includes("appointment not found for this business")
    )
  );
}

function getBusinessIdFromStorage() {
  try {
    return window?.localStorage?.getItem("current_business_id") || null;
  } catch {
    return null;
  }
}

function prettyBytes(n) {
  const v = Number(n || 0);
  if (!isFinite(v) || v <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i += 1;
  }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function isImageFile(file) {
  const t = String(file?.type || "").toLowerCase();
  return t.startsWith("image/");
}

function isImageAttachment(att) {
  const ct = String(
    att?.original_content_type ||
    att?.originalContentType ||
    att?.content_type ||
    att?.contentType ||
    att?.mime ||
    ""
  ).toLowerCase();

  if (ct.startsWith("image/")) return true;

  // fallback: dacă nu avem ct, încercăm din url / nume
  const s = String(att?.url || att?.original_name || att?.name || "").toLowerCase();
  return (
    s.includes(".webp") ||
    s.includes(".jpg") ||
    s.includes(".jpeg") ||
    s.includes(".png") ||
    s.includes(".gif") ||
    s.includes(".bmp")
  );
}

/**
 * Thumbnail pentru attachment în context staff:
 * - nu putem folosi direct <img src={att.url}> pentru că endpoint-ul staff cere X-Business-Id
 * - folosim getStaffAttachmentBlob(id) -> objectURL + cache
 */
function StaffAttachmentThumb({ att, businessId, cacheRef, bump }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState("");

  useEffect(() => {
    let alive = true;

    const id = att?.id ?? att?.attachment_id ?? att?.attachmentId;
    if (!id) return;

    const cached = cacheRef.current.get(String(id));
    if (cached?.url) {
      setSrc(cached.url);
      return;
    }

    (async () => {
      try {
        const blob = await getStaffAttachmentBlob(id, { businessId });
        if (!alive) return;

        const url = URL.createObjectURL(blob);
        cacheRef.current.set(String(id), { url });
        setSrc(url);
        bump((x) => x + 1);
      } catch {
        // ignore (nu blocăm UI)
      }
    })();

    return () => {
      alive = false;
    };
  }, [att, businessId, cacheRef, bump]);

  if (!src) {
    return (
      <div className="h-24 w-24 rounded-lg border border-border bg-muted animate-pulse" />
    );
  }

  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="block h-24 w-24 overflow-hidden rounded-lg border border-border hover:opacity-90"
      title={t("chat.openImage")}
    >
      <img
        src={src}
        alt={att?.original_name || att?.name || "attachment"}
        className="h-full w-full object-cover"
        loading="lazy"
      />
    </a>
  );
}

/**
 * Props:
 *  - isOpen
 *  - appointmentId
 *  - businessId (optional)  -> dacă nu există, fallback la current_business_id
 *  - onClose
 */
export default function AppointmentChatModal({ isOpen, appointmentId, businessId, onClose }) {
  const { t } = useTranslation();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");

  const [pendingFiles, setPendingFiles] = useState([]); // File[]
  const [pendingPreviews, setPendingPreviews] = useState([]); // { file, url }[]
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const bottomRef = useRef(null);

  // cache pentru objectURL-urile attachments (staff)
  const attachmentUrlCacheRef = useRef(new Map()); // Map<string, {url:string}>
  const [, forceRerender] = useState(0);

  const effectiveBusinessId = useMemo(() => {
    return businessId || getBusinessIdFromStorage();
  }, [businessId]);

  const scrollToBottom = (smooth = true) => {
    const el = bottomRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "end" });
    } catch {
      // ignore
    }
  };

  const revokeAllAttachmentUrls = () => {
    try {
      for (const v of attachmentUrlCacheRef.current.values()) {
        if (v?.url) URL.revokeObjectURL(v.url);
      }
    } catch {
      // ignore
    }
    attachmentUrlCacheRef.current.clear();
  };

  const clearPendingFiles = () => {
    try {
      pendingPreviews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
    } catch {
      // ignore
    }
    setPendingPreviews([]);
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const ensureThread = async () => {
    if (!appointmentId) return null;

    // 1) încercăm thread
    try {
      const t1 = unwrap(await getStaffThread(appointmentId, { businessId: effectiveBusinessId }));
      setThread(t1 || null);
      return t1 || null;
    } catch (e1) {
      if (isApptNotFoundForBusinessError(e1)) throw e1;
      // continuăm
    }

    // 2) încercăm invite (creează thread)
    try {
      await createChatInvite(appointmentId, { sendSms: false }, { businessId: effectiveBusinessId });
    } catch (e2) {
      if (isApptNotFoundForBusinessError(e2)) throw e2;
      // ok
    }

    // 3) recitim thread
    const t2 = unwrap(await getStaffThread(appointmentId, { businessId: effectiveBusinessId }));
    setThread(t2 || null);
    return t2 || null;
  };

  const loadMessages = async () => {
    if (!appointmentId) return;

    const res = await getStaffMessages(
      appointmentId,
      { limit: 50 },
      { businessId: effectiveBusinessId }
    );

    const arr = unwrap(res);
    setMessages(Array.isArray(arr) ? arr : Array.isArray(arr?.results) ? arr.results : []);

    try {
      await markStaffRead(appointmentId, { businessId: effectiveBusinessId });
    } catch {
      // ignore
    }
  };

  const loadAll = async () => {
    if (!appointmentId) return;

    setLoading(true);
    setError("");

    try {
      await ensureThread();
      await loadMessages();
      setTimeout(() => scrollToBottom(false), 50);
    } catch (e) {
      console.error("Chat load error:", e?.response?.data || e);

      if (isApptNotFoundForBusinessError(e)) {
        setError(t("chat.wrongBusinessError"));
      } else {
        setError(normalizeError(e, t("chat.loadError")));
      }
    } finally {
      setLoading(false);
    }
  };

  // încarcă thread + mesaje la deschiderea chat-ului
  // (înainte, istoricul apărea abia după ce trimiteai un mesaj)
  useEffect(() => {
    if (!isOpen) return;
    if (!appointmentId) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appointmentId, effectiveBusinessId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!appointmentId) return;
    if (!effectiveBusinessId) return;

    const sock = connectStaffChatSocket({
      businessId: effectiveBusinessId,
      appointmentOrThreadId: appointmentId,
      onEvent: (ev) => {
        if (!ev || typeof ev !== "object") return;

        if (ev.type === "chat.message") {
          const msg = ev?.message;
          if (!msg || !msg.id) return;

          setMessages((prev) => {
            const arr = Array.isArray(prev) ? prev : [];
            if (arr.some((x) => x?.id === msg.id)) return arr;
            return [...arr, msg];
          });

          // best-effort mark read (keeps server markers fresh)
          markStaffRead(appointmentId, { businessId: effectiveBusinessId }).catch(() => {});
        }
      },
    });

    return () => {
      try {
        sock?.close?.();
      } catch {}
    };
  }, [isOpen, appointmentId, effectiveBusinessId]);

useEffect(() => {
    if (!isOpen) return;
    scrollToBottom(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages?.length, isOpen]);

  // cleanup când se închide modalul
  useEffect(() => {
    if (isOpen) return;
    clearPendingFiles();
    revokeAllAttachmentUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // cleanup la unmount
  useEffect(() => {
    return () => {
      clearPendingFiles();
      revokeAllAttachmentUrls();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickFiles = (e) => {
    const list = Array.from(e?.target?.files || []);
    if (!list.length) return;

    // guardrails simple (poți ajusta)
    const MAX_FILES_TOTAL = 5;
    const MAX_BYTES_EACH = 10 * 1024 * 1024; // 10MB

    const incoming = list.filter((f) => isImageFile(f));
    const filtered = incoming.filter((f) => (f?.size || 0) <= MAX_BYTES_EACH);

    const nextFiles = [...pendingFiles, ...filtered].slice(0, MAX_FILES_TOTAL);

    // refacem previews pentru nextFiles
    // (revoke pe cele vechi, ca să evităm leaks)
    pendingPreviews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));

    const nextPreviews = nextFiles.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
    }));

    setPendingFiles(nextFiles);
    setPendingPreviews(nextPreviews);

    // permit reselect același fișier
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePendingAt = (idx) => {
    const prev = pendingPreviews[idx];
    if (prev?.url) {
      try {
        URL.revokeObjectURL(prev.url);
      } catch {
        // ignore
      }
    }

    const nextFiles = pendingFiles.filter((_, i) => i !== idx);

    // rebuild previews
    pendingPreviews.forEach((p, i) => {
      if (i !== idx && p?.url) {
        try {
          URL.revokeObjectURL(p.url);
        } catch {
          // ignore
        }
      }
    });

    const nextPreviews = nextFiles.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
    }));

    setPendingFiles(nextFiles);
    setPendingPreviews(nextPreviews);
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();

    if (!appointmentId) return;

    const text = (body || "").trim();
    const files = pendingFiles || [];

    // permitem mesaj doar cu poze, doar cu text, sau ambele
    if (!text && !files.length) return;

    setSending(true);
    setError("");

    try {
      await postStaffMessage(
        appointmentId,
        { body: text || "" },
        { businessId: effectiveBusinessId, files }
      );

      setBody("");
      clearPendingFiles();

      await loadMessages();
      scrollToBottom(true);
    } catch (e2) {
      console.error("Chat send error:", e2?.response?.data || e2);

      if (isApptNotFoundForBusinessError(e2)) {
        setError(t("chat.wrongBusinessSendError"));
      } else {
        setError(normalizeError(e2, t("chat.sendError")));
      }
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full bg-card text-foreground border border-border shadow-xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg">
        {/* Header */}
        <div className="border-b px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-lg font-semibold text-foreground">{t("chat.appointmentChat")}</div>
              {thread?.title ? (
                <div className="text-xs text-muted-foreground truncate">{thread.title}</div>
              ) : null}
            </div>

            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={onClose}
              aria-label={t("common.close")}
            >
              ✕
            </button>
          </div>

          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              fullWidth
              className="sm:w-auto"
              onClick={async () => {
                try {
                  const res = await createChatInvite(
                    appointmentId,
                    { sendSms: false },
                    { businessId: effectiveBusinessId }
                  );

                  const data = res?.data ?? res;
                  const token = data?.client_token || data?.clientToken || data?.token || "";
                  const urlFromApi = data?.client_url || data?.clientUrl || "";
                  const publicUrl = token ? `${window.location.origin}/chat/${token}` : urlFromApi;

                  if (!publicUrl) {
                    alert(t("chat.noLinkToken"));
                    return;
                  }

                  if (navigator.clipboard) {
                    await navigator.clipboard.writeText(publicUrl);
                    alert(t("chat.linkCopied"));
                  } else {
                    alert(publicUrl);
                  }
                } catch (e) {
                  console.error(e?.response?.data || e);
                  alert(t("chat.generateLinkError"));
                }
              }}
            >
              {t("chat.generateLink")}
            </Button>

            <Button
              type="button"
              variant="outline"
              fullWidth
              className="sm:w-auto"
              onClick={async () => {
                try {
                  const res = await createChatInvite(
                    appointmentId,
                    { sendSms: true },
                    { businessId: effectiveBusinessId }
                  );

                  const data = res?.data ?? res;
                  const token = data?.client_token || data?.clientToken || data?.token || "";
                  const urlFromApi = data?.client_url || data?.clientUrl || "";
                  const publicUrl = token ? `${window.location.origin}/chat/${token}` : urlFromApi;

                  if (publicUrl && navigator.clipboard) {
                    await navigator.clipboard.writeText(publicUrl);
                  }

                  alert(publicUrl ? t("chat.inviteSmsOk") : t("chat.inviteOk"));
                } catch (e) {
                  console.error(e?.response?.data || e);
                  alert(t("chat.smsSendError"));
                }
              }}
            >
              {t("chat.generateLinkSms")}
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {error ? (
            <div className="mb-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="text-sm text-gray-500">{t("sms.loading")}</div>
          ) : messages.length ? (
            <div className="space-y-3">
              {messages.map((m, idx) => {
                const senderType = (m.sender_type || m.senderType || "")
                  .toString()
                  .toLowerCase();

                const isClient =
                  senderType === "client" ||
                  String(
                    m.sender_name ||
                    m.senderName ||
                    m.author_name ||
                    m.username ||
                    ""
                  )
                    .toLowerCase()
                    .trim() === "client";

                const who =
                  m.sender_name ||
                  m.sender?.name ||
                  m.author_name ||
                  m.author?.name ||
                  m.username ||
                  (isClient ? "Client" : "Staff");

                const when = fmtTime(m.created_at || m.created || m.ts);
                const text2 = m.body || m.text || "";

                const attachments = Array.isArray(m.attachments)
                  ? m.attachments
                  : Array.isArray(m.files)
                    ? m.files
                    : [];

                const key = m.id ?? `${idx}-${when}-${String(text2).slice(0, 10)}`;

                return (
                  <div
                    key={key}
                    className={`flex ${isClient ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={[
                        "max-w-[78%] rounded-2xl border border-border px-3 py-2",
                        isClient ? "bg-background" : "bg-muted",
                      ].join(" ")}
                    >
                      <div className="mb-1 flex items-center justify-between gap-3">
                        <div className="text-xs font-medium text-foreground/90">{who}</div>
                        <div className="text-[11px] text-muted-foreground">{when}</div>
                      </div>

                      {text2 ? (
                        <div className="text-sm text-foreground whitespace-pre-wrap">{text2}</div>
                      ) : null}

                      {attachments?.length ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {attachments
                            .filter((a) => isImageAttachment(a))
                            .map((att) => {
                              const id = att?.id ?? att?.attachment_id ?? att?.attachmentId;
                              if (!id) return null;
                              return (
                                <StaffAttachmentThumb
                                  key={String(id)}
                                  att={att}
                                  businessId={effectiveBusinessId}
                                  cacheRef={attachmentUrlCacheRef}
                                  bump={forceRerender}
                                />
                              );
                            })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          ) : (
            <div className="text-sm text-gray-500">{t("chat.noMessagesYet")}</div>
          )}
        </div>

        {/* Composer */}
        <form onSubmit={handleSend} className="border-t border-border px-4 sm:px-6 py-3 sm:py-4 bg-card">
          {/* hidden input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handlePickFiles}
          />

          {pendingPreviews.length ? (
            <div className="mb-3">
              <div className="mb-2 text-xs text-gray-500">
                {t("chat.attachmentsInfo", { count: pendingPreviews.length })}
              </div>

              <div className="flex flex-wrap gap-2">
                {pendingPreviews.map((p, i) => (
                  <div
                    key={`${p.file?.name || "file"}-${i}`}
                    className="relative h-20 w-20 overflow-hidden rounded-lg border"
                  >
                    <img
                      src={p.url}
                      alt={p.file?.name || "preview"}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      className="absolute right-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-black/80"
                      onClick={() => removePendingAt(i)}
                      title={t("chat.removeAttachment")}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-2 text-[11px] text-gray-400">
                {t("chat.attachmentTip")}
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              className="rounded-lg border border-border px-3 py-2 text-sm bg-background hover:bg-muted shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              title={t("chat.addPhotos")}
            >
              📎
            </button>

            <input
              type="text"
              className="flex-1 min-w-[180px] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              placeholder={t("chat.messagePlaceholder")}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
            />

            <button
              type="submit"
              className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-60 shrink-0"
              disabled={sending || (!body.trim() && !pendingFiles.length)}
            >
              {sending ? "..." : t("chat.send")}
            </button>
          </div>

          {pendingFiles.length ? (
            <div className="mt-2 text-[11px] text-gray-400">
              {t("chat.selectedFiles")}{" "}
              {pendingFiles
                .map((f) => `${f.name} (${prettyBytes(f.size)})`)
                .slice(0, 3)
                .join(", ")}
              {pendingFiles.length > 3 ? ` +${pendingFiles.length - 3}` : ""}
              <button
                type="button"
                className="ml-2 underline hover:text-gray-600"
                onClick={clearPendingFiles}
                disabled={sending}
              >
                {t("chat.clearFiles")}
              </button>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}
